<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrganizationStore } from '@/stores/organizationStore'

const route = useRoute()
const router = useRouter()
const orgStore = useOrganizationStore()

const organizationId = route.params.id
const teamId = route.params.teamId

// Local State
const isLoading = ref(false)
const isEditingName = ref(false)
const editNameValue = ref('')
const nameSaveError = ref('')

const isAddingMember = ref(false)
const selectedMemberId = ref('')
const selectedRole = ref('group_member')
const addUserError = ref('')

// Autocomplete State
const memberSearchQuery = ref('')
const showMemberDropdown = ref(false)

// Available Roles for Teams
const teamRoles = [
  { label: 'Member', value: 'group_member' },
  { label: 'Admin', value: 'group_admin' }
]

// --- Computed Data ---

const team = computed(() => orgStore.getTeamById(teamId))
const teamMembers = computed(() => orgStore.activeTeamMembers)
const allOrgMembers = computed(() => orgStore.users)

// Filter available members (All Org Members - Current Team Members)
const availableMembersToAdd = computed(() => {
  const currentIds = new Set(teamMembers.value.map(m => m.id))
  return allOrgMembers.value.filter(m => !currentIds.has(m.id))
})

// Filter for Autocomplete based on user input
const filteredMembers = computed(() => {
  if (!memberSearchQuery.value) return availableMembersToAdd.value
  const query = memberSearchQuery.value.toLowerCase()
  return availableMembersToAdd.value.filter(m =>
    m.email.toLowerCase().includes(query) ||
    m.name.toLowerCase().includes(query)
  )
})

// --- Lifecycle ---

onMounted(async () => {
  isLoading.value = true
  try {
    if (orgStore.teams.length === 0) await orgStore.fetchTeams(organizationId)
    if (orgStore.users.length === 0) await orgStore.fetchUsers(organizationId)

    await orgStore.fetchTeamMembers(organizationId, teamId)

    if (team.value) editNameValue.value = team.value.name
  } catch (err) {
    console.error("Failed to load team data", err)
  } finally {
    isLoading.value = false
  }
})

watch(team, (newTeam) => {
  if (newTeam && !isEditingName.value) {
    editNameValue.value = newTeam.name
  }
})

// --- Handlers ---

const startEditingName = () => {
  editNameValue.value = team.value?.name || ''
  isEditingName.value = true
  nameSaveError.value = ''
}

const saveName = async () => {
  if (!editNameValue.value.trim()) return

  try {
    await orgStore.updateTeam(organizationId, teamId, editNameValue.value)
    isEditingName.value = false
  } catch (err) {
    nameSaveError.value = 'Failed to update name'
  }
}

const cancelEditingName = () => {
  isEditingName.value = false
  editNameValue.value = team.value?.name || ''
  nameSaveError.value = ''
}

const selectMemberToAdd = (member) => {
  selectedMemberId.value = member.id
  memberSearchQuery.value = member.email
  showMemberDropdown.value = false
}

// Handle blur with delay to allow click event on dropdown item to fire
const handleSearchBlur = () => {
  setTimeout(() => {
    showMemberDropdown.value = false
  }, 200)
}

const handleAddMember = async () => {
  if (!selectedMemberId.value) return

  isAddingMember.value = true
  addUserError.value = ''

  try {
    await orgStore.addTeamMember(organizationId, teamId, selectedMemberId.value, selectedRole.value)

    // Reset selections
    selectedMemberId.value = ''
    memberSearchQuery.value = ''
    selectedRole.value = 'group_member'
  } catch (err) {
    addUserError.value = err.response?.data?.error?.message || 'Failed to add member'
  } finally {
    isAddingMember.value = false
  }
}

const handleRemoveMember = async (member) => {
  if (!confirm(`Remove ${member.name} from this team?`)) return
  try {
    await orgStore.removeTeamMember(organizationId, teamId, member.id)
  } catch (err) {
    console.error(err)
    alert("Failed to remove member")
  }
}

const deleteTeam = async () => {
  if (!confirm('Are you sure you want to delete this team? This action cannot be undone.')) return
  try {
    await orgStore.deleteTeam(organizationId, teamId)
    router.push(`/organizations/${organizationId}/teams`)
  } catch (err) {
    alert("Failed to delete team")
  }
}

const formatRole = (role) => {
  if (!role) return 'Member'
  return role.replace('group_', '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
}
</script>

<template>
  <div class="space-y-12">
    <div class="border-b border-b-gray-100 pb-4 flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0">
      <div class="space-y-1 w-full sm:w-auto">
        <div class="flex flex-wrap items-center gap-2">
          <router-link :to="`/organizations/${organizationId}/teams`"
            class="text-xl text-gray-900 hover:text-gray-700">Team</router-link> /

          <div v-if="isEditingName" class="flex items-center gap-2">
            <input v-model="editNameValue" @keyup.enter="saveName" @keyup.esc="cancelEditingName"
              class="px-2 py-1 border border-gray-300 rounded text-xl text-gray-900 focus:outline-none focus:ring-1 focus:ring-black h-8 w-64"
              autoFocus />
            <button @click="saveName" class="text-xs bg-black text-white px-2 py-1 rounded">Save</button>
            <button @click="cancelEditingName" class="text-xs text-gray-500 px-2 py-1">Cancel</button>
          </div>

          <h1 v-else @click="startEditingName"
            class="text-xl text-gray-900 font-bold cursor-pointer hover:underline decoration-dashed underline-offset-4"
            title="Click to edit name">
            {{ team?.name || 'Loading...' }}
          </h1>
        </div>
        <p v-if="nameSaveError" class="text-xs text-red-500 mt-1">{{ nameSaveError }}</p>
        <p class="text-md font-light text-gray-500 mt-1">Manage team members and settings</p>
      </div>

      <button @click="deleteTeam" class="text-red-600 text-sm hover:underline">
        Delete Team
      </button>
    </div>

    <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden min-h-40">
      <div class="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
        <h2 class="text-lg text-gray-900">Members</h2>
        <span v-if="isLoading" class="text-xs text-gray-400">Loading...</span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead>
            <tr class="border-b border-gray-100">
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Role
              </th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Name
              </th>
              <th
                class="px-6 py-4 font-extrabold text-xs text-gray-900 uppercase tracking-wide underline decoration-2 underline-offset-4 decoration-gray-900">
                Email
              </th>
              <th class="px-6 py-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="teamMembers.length === 0 && !isLoading">
              <td colspan="4" class="px-6 py-8 text-center text-gray-500">
                No members in this team.
              </td>
            </tr>
            <tr v-for="member in teamMembers" :key="member.id"
              class="bg-white hover:bg-gray-50/50 transition border-b border-gray-50 last:border-0">
              <td class="px-6 py-2 font-medium text-gray-900 capitalize">
                {{ formatRole(member.meta?.role) }}
              </td>
              <td class="px-6 py-2 font-medium text-gray-900">{{ member.name }}</td>
              <td class="px-6 py-2 font-medium text-gray-900">{{ member.email }}</td>
              <td class="px-6 py-2 text-right">
                <button @click="handleRemoveMember(member)"
                  class="p-1.5 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded transition"
                  :title="`Remove ${member.email} from team`">
                  <svg class=" w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"></path>
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="p-4 bg-gray-50 border-t border-gray-100">
        <p v-if="addUserError" class="mb-2 text-xs text-red-600 font-medium px-1">{{ addUserError }}</p>

        <form @submit.prevent="handleAddMember" class="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">

          <div class="relative w-32 shrink-0">
            <select v-model="selectedRole"
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9 bg-white">
              <option v-for="role in teamRoles" :key="role.value" :value="role.value">
                {{ role.label }}
              </option>
            </select>
          </div>

          <div class="grow relative">
            <input type="text" v-model="memberSearchQuery" @focus="showMemberDropdown = true"
              @input="showMemberDropdown = true" @blur="handleSearchBlur" placeholder="Search by email..."
              class="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-black focus:border-black h-9 bg-white" />

            <div v-if="showMemberDropdown && filteredMembers.length > 0"
              class="absolute bottom-full left-0 w-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
              <ul>
                <li v-for="m in filteredMembers" :key="m.id" @mousedown="selectMemberToAdd(m)"
                  class="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm flex flex-col">
                  <span class="font-medium text-gray-900">{{ m.email }}</span>
                  <span class="text-xs text-gray-500">{{ m.name }}</span>
                </li>
              </ul>
            </div>

            <div v-if="showMemberDropdown && filteredMembers.length === 0 && memberSearchQuery"
              class="absolute bottom-full left-0 w-full mb-1 bg-white border border-gray-200 rounded-md shadow-lg p-3 text-sm text-gray-500 z-50">
              No matching members found.
            </div>
          </div>

          <button type="submit" :disabled="!selectedMemberId || isAddingMember"
            class="bg-black text-white px-4 py-2 rounded-md text-sm font-bold hover:bg-gray-800 transition flex items-center h-9 disabled:opacity-70 disabled:cursor-not-allowed">
            <svg v-if="isAddingMember" class="animate-spin -ml-1 mr-2 h-3.5 w-3.5 text-white"
              xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
              </path>
            </svg>
            <svg v-else class="w-3.5 h-3.5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 4v16m8-8H4" />
            </svg>
            Add member
          </button>
        </form>

        <p v-if="availableMembersToAdd.length === 0 && allOrgMembers.length > 0"
          class="text-xs text-gray-500 mt-2 ml-1">
          All organization members are already in this team.
        </p>
      </div>
    </div>
  </div>
</template>